import { APP_CONFIG_DEFAULTS } from '../app-config';
import { BookingDetails } from '../components/ChatInterface';

export interface HistoryMessage {
  id: string | number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  intent?: string;
}

export interface SessionHistoryResponse {
  session_id: string;
  history: HistoryMessage[];
}

export interface ChatResponse {
  response: string;
  intent?: string;
  session_id?: string;
}

type ChatRequestBody = {
  message: string;
  session_id?: string | null;
  userName?: string;
  userDetails?: Record<string, any> | null;
  files?: any;
};

type CreateSessionRequest = {
  name: string;
  company?: string;
};

type CreateSessionResponse = {
  session_id: string;
  message?: string;
};
type VerifyOtpRequest = {
  otp: string;
};

type VerifyOtpResponse = {
  session_id: string;
  message?: string;
  pending_message?: string;
  status?: string;
};
type UpdateProfileRequest = {
  profileDetails: BookingDetails;
};

type UpdateProfileResponse = {
  message?: string;
  session_id?: string;
  status?: string;
};

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}


export class ApiService {

  private sessionId: string | null = null;
  private apiKey: string | null = null;
  private companyName: string | null = null;
  private debug: boolean | null = null;
  private BASE: string | null = null;

  constructor(apiKey: string = "", companyName: string = "", debug: boolean = false) {
    this.apiKey = apiKey;
    this.companyName = companyName;
    this.debug = debug;
    this.setServer();
  }

  public init(apiKey?: string, companyName?: string, debug?: boolean) {
    if (typeof apiKey !== 'undefined') this.apiKey = apiKey || null;
    if (typeof companyName !== 'undefined') this.companyName = companyName || null;
    if (typeof debug !== 'undefined') this.debug = debug ?? this.debug;
    this.setServer();
  }

  private setServer() {
    const company = this.companyName ?? '';
    // Use VITE_API_BASE_URL if set, otherwise fallback to APP_CONFIG_DEFAULTS.backendUrl
    const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) || APP_CONFIG_DEFAULTS.backendUrl || '/api';
    this.BASE = `${API_BASE.replace(/\/$/, '')}/api`;

    if (!this.BASE.startsWith('http')) {
      console.warn('[SmartAgent] Backend URL might be invalid:', this.BASE);
    }
  }

  private async request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
    const res = await fetch(input, {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      ...init,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `API error: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as T;
  }

  public saveSessionId(id: string | null) {
    if (!id) {
      console.warn('Attempted to save null session ID');
      return;
    }
    this.sessionId = id;
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public clearSession() {
    this.sessionId = null;
  }

  // POST -> SEND MESSAGE
  public async sendMessage(message: string, opts?: { userName?: string; userDetails?: any; files?: any; sessionId?: string | null }): Promise<ChatResponse> {
    const body: ChatRequestBody = {
      message,
      session_id: opts?.sessionId ?? this.getSessionId() ?? null,
      userName: opts?.userName,
      userDetails: opts?.userDetails ?? null,
      files: opts?.files,
    };

    const resp = await this.request<ChatResponse>(`${this.BASE}/chat`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (resp.session_id) {
      this.saveSessionId(resp.session_id);
    }

    return resp;
  }

  // POST -> CREATE SESSION WITH NAME
  public async createSession(reqName: string, company?: string): Promise<CreateSessionResponse> {
    const payload: CreateSessionRequest = { name: reqName, company };
    const resp = await this.request<CreateSessionResponse>(`${this.BASE}/session`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (resp.session_id) {
      this.saveSessionId(resp.session_id);
    }

    return resp;
  }

  // POST -> CREATE FORM DETAILS FOR SESSION
  public async createFormDetails(session_id: string, reqName: string, company?: string): Promise<CreateSessionResponse> {
    const payload: CreateSessionRequest = { name: reqName, company };
    const resp = await this.request<CreateSessionResponse>(`${this.BASE}/session/${encodeURIComponent(session_id)}/history`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (resp.session_id) {
      this.saveSessionId(resp.session_id);
    }
    return resp;
  }
  // POST -> Update contact details FOR SESSION
  public async updateProfile(session_id: string, profileDetails: BookingDetails): Promise<UpdateProfileResponse> {
    const payload: BookingDetails = profileDetails;
    const resp = await this.request<UpdateProfileResponse>(`${this.BASE}/session/${encodeURIComponent(session_id)}/profile`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (resp.session_id) {
      this.saveSessionId(resp.session_id);
    }
    return resp;
  }
  // POST -> Verify OTP FOR SESSION
  public async verifyOtp(session_id: string, otp: string): Promise<VerifyOtpResponse> {
    const payload: VerifyOtpRequest = { otp };
    const resp = await this.request<VerifyOtpResponse>(`${this.BASE}/session/${encodeURIComponent(session_id)}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (resp.session_id) {
      this.saveSessionId(resp.session_id);
    }
    return resp;
  }

  // GET -> FETCH SESSION HISTORY
  public async getSessionHistory(sessionId: string): Promise<SessionHistoryResponse> {
    const resp = await this.request<SessionHistoryResponse>(`${this.BASE}/session/${encodeURIComponent(sessionId)}/history`, {
      method: 'GET',
    });
    if (resp.session_id) this.saveSessionId(resp.session_id);
    return resp;
  }

  // Backwards-compatible alias expected by components
  public async createSessionWithName(name: string, company?: string) {
    return this.createSession(name, company);
  }

  // POST -> Speech to Text
  public async transcribeAudio(audioBlob: Blob): Promise<{ status: string; transcript: string }> {
    const formData = new FormData();
    formData.append("audio_file", audioBlob, "voice.webm");

    const res = await fetch(`${this.BASE}/transcribe`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Transcribe error: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as { status: string; transcript: string };
  }

  // POST -> Text to Speech
  public async textToSpeech(text: string, character = "Mason-PlayAI", response_format: "wav" | "mp3" = "wav"): Promise<Blob> {
    const body = {
      text,
      character,
      response_format,
    };

    const res = await fetch(`${this.BASE}/speech/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const textErr = await res.text().catch(() => "");
      throw new Error(textErr || `speech error: ${res.status} ${res.statusText}`);
    }

    return await res.blob();
  }

  // POST -> Stream Text to Speech
  public async streamTextToSpeech(text: string, character = "Mason-PlayAI", response_format: "wav" | "mp3" = "mp3"): Promise<ReadableStream<Uint8Array>> {
    const body = {
      text,
      character,
      response_format,
    };

    const res = await fetch(`${this.BASE}/speech/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const textErr = await res.text().catch(() => "");
      throw new Error(textErr || `speech error: ${res.status} ${res.statusText}`);
    }

    if (!res.body) {
      throw new Error("Response body is null");
    }

    return res.body;
  }

  async validateApiKey(): Promise<ApiResponse<{ valid: boolean; plan: string; limits: any }>> {
    return this.request('/auth/validate', {
      method: 'POST',
    });
  }

}

export const chatService = new ApiService();
export default chatService;